import type { AutoTaskState, TaskData } from '@/lib/dataSync';
import type { PlantingLog } from '@/lib/plantingTracker';
import {
  readTaskRecurrenceState,
  writeTaskRecurrenceState,
  type TaskRecurrenceState,
} from '@/lib/guidance/taskState';

type AutoTaskIdClassification =
  | { kind: 'current-log'; id: string; plantingLogId: string }
  | { kind: 'orphaned-log'; id: string; plantingLogId: string }
  | { kind: 'garden-level'; id: string }
  | { kind: 'non-auto'; id: string }
  | { kind: 'unknown-auto'; id: string };

export interface AutoTaskPruneResult {
  taskState: TaskData;
  changed: boolean;
  removedIds: string[];
  preservedGardenLevelIds: string[];
  preservedNonAutoIds: string[];
  preservedUnknownAutoIds: string[];
}

export interface RecurrencePruneResult {
  recurrence: TaskRecurrenceState;
  changed: boolean;
  removedIds: string[];
  preservedGardenLevelIds: string[];
  preservedNonAutoIds: string[];
  preservedUnknownAutoIds: string[];
}

function isTaskStateDebugEnabled(): boolean {
  return (
    process.env.NODE_ENV !== 'production' &&
    typeof window !== 'undefined' &&
    window.localStorage.getItem('garden-task-state-debug') === '1'
  );
}

function currentPlantingLogIds(plantingLogs: PlantingLog[]): string[] {
  return plantingLogs
    .map(log => log.id)
    .filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
    .sort((a, b) => b.length - a.length);
}

export function classifyAutoTaskId(
  id: string,
  plantingLogs: PlantingLog[],
): AutoTaskIdClassification {
  if (!id.startsWith('auto-')) return { kind: 'non-auto', id };
  if (id.startsWith('auto-garden-')) return { kind: 'garden-level', id };

  const body = id.slice('auto-'.length);
  for (const plantingLogId of currentPlantingLogIds(plantingLogs)) {
    if (body.startsWith(`${plantingLogId}-`)) {
      return { kind: 'current-log', id, plantingLogId };
    }
  }

  // Current and legacy planting-log ids created by the app are timestamp-random
  // strings such as 1777340145073-09an5. Only prune when that shape is clear.
  const legacyLogMatch = body.match(/^(\d{10,}-[a-z0-9]+)-/i);
  if (legacyLogMatch) {
    return { kind: 'orphaned-log', id, plantingLogId: legacyLogMatch[1] };
  }

  // Some early logs reused generated plant ids as planting-log ids. If an old
  // auto-task follows a known log-task suffix, it is safe to identify the
  // deleted log portion without guessing from plant names.
  const knownSuffixMatch = body.match(
    /^(.+)-(frost|temp|hard-frost|wrong-season|dormancy|water|soil-check|crop|seasonal-harvest-window|seasonal-harvest-check|harvest-now|harvest-approaching|harvest-urgent|fertilize|spring-topdress|dormant-prune|grape-prune|cane-prune|fall-mulch)-/,
  );
  if (knownSuffixMatch) {
    return { kind: 'orphaned-log', id, plantingLogId: knownSuffixMatch[1] };
  }

  return { kind: 'unknown-auto', id };
}

function shouldPreserveTaskId(id: string, plantingLogs: PlantingLog[], result: {
  removedIds: string[];
  preservedGardenLevelIds: string[];
  preservedNonAutoIds: string[];
  preservedUnknownAutoIds: string[];
}): boolean {
  const classification = classifyAutoTaskId(id, plantingLogs);
  switch (classification.kind) {
    case 'current-log':
      return true;
    case 'garden-level':
      result.preservedGardenLevelIds.push(id);
      return true;
    case 'non-auto':
      result.preservedNonAutoIds.push(id);
      return true;
    case 'unknown-auto':
      result.preservedUnknownAutoIds.push(id);
      return true;
    case 'orphaned-log':
      result.removedIds.push(id);
      return false;
  }
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values));
}

function pruneStringArray(
  ids: string[] | undefined,
  plantingLogs: PlantingLog[],
  result: Pick<AutoTaskPruneResult, 'removedIds' | 'preservedGardenLevelIds' | 'preservedNonAutoIds' | 'preservedUnknownAutoIds'>,
): string[] {
  return unique(ids ?? []).filter(id => shouldPreserveTaskId(id, plantingLogs, result));
}

function pruneStringRecord(
  record: Record<string, string> | undefined,
  plantingLogs: PlantingLog[],
  result: Pick<AutoTaskPruneResult, 'removedIds' | 'preservedGardenLevelIds' | 'preservedNonAutoIds' | 'preservedUnknownAutoIds'>,
): Record<string, string> {
  const next: Record<string, string> = {};
  for (const [id, value] of Object.entries(record ?? {})) {
    if (shouldPreserveTaskId(id, plantingLogs, result)) next[id] = value;
  }
  return next;
}

export function pruneOrphanedAutoTaskState(
  taskState: TaskData,
  plantingLogs: PlantingLog[],
): AutoTaskPruneResult {
  const result: AutoTaskPruneResult = {
    taskState,
    changed: false,
    removedIds: [],
    preservedGardenLevelIds: [],
    preservedNonAutoIds: [],
    preservedUnknownAutoIds: [],
  };

  const nextState: TaskData = {
    checked: taskState.checked ?? [],
    custom: taskState.custom ?? [],
    autoChecked: pruneStringArray(taskState.autoChecked, plantingLogs, result),
    autoDismissed: pruneStringArray(taskState.autoDismissed, plantingLogs, result),
    autoOverrides: pruneStringRecord(taskState.autoOverrides, plantingLogs, result),
    autoArchived: pruneStringRecord(taskState.autoArchived, plantingLogs, result),
  };

  result.removedIds = unique(result.removedIds);
  result.preservedGardenLevelIds = unique(result.preservedGardenLevelIds);
  result.preservedNonAutoIds = unique(result.preservedNonAutoIds);
  result.preservedUnknownAutoIds = unique(result.preservedUnknownAutoIds);
  result.changed = result.removedIds.length > 0;
  result.taskState = nextState;
  return result;
}

export function toAutoTaskState(taskState: TaskData): AutoTaskState {
  return {
    autoChecked: taskState.autoChecked ?? [],
    autoDismissed: taskState.autoDismissed ?? [],
    autoOverrides: taskState.autoOverrides ?? {},
    autoArchived: taskState.autoArchived ?? {},
  };
}

export function pruneOrphanedTaskRecurrence(
  plantingLogs: PlantingLog[],
): RecurrencePruneResult {
  const recurrence = readTaskRecurrenceState();
  const result: RecurrencePruneResult = {
    recurrence,
    changed: false,
    removedIds: [],
    preservedGardenLevelIds: [],
    preservedNonAutoIds: [],
    preservedUnknownAutoIds: [],
  };

  const next: TaskRecurrenceState = {};
  for (const [id, entry] of Object.entries(recurrence)) {
    if (shouldPreserveTaskId(id, plantingLogs, result)) next[id] = entry;
  }

  result.removedIds = unique(result.removedIds);
  result.preservedGardenLevelIds = unique(result.preservedGardenLevelIds);
  result.preservedNonAutoIds = unique(result.preservedNonAutoIds);
  result.preservedUnknownAutoIds = unique(result.preservedUnknownAutoIds);
  result.changed = result.removedIds.length > 0;
  result.recurrence = next;
  if (result.changed) writeTaskRecurrenceState(next);
  return result;
}

export function logTaskStateHygieneDiagnostics(
  label: string,
  plantingLogs: PlantingLog[],
  taskState: TaskData,
  pruneResult?: AutoTaskPruneResult,
  recurrenceResult?: RecurrencePruneResult,
): void {
  if (!isTaskStateDebugEnabled()) return;

  const recurrence = readTaskRecurrenceState();
  console.info(`[garden:task-state-debug] ${label}`, {
    plantingLogIds: currentPlantingLogIds(plantingLogs),
    autoCheckedIds: taskState.autoChecked ?? [],
    autoDismissedIds: taskState.autoDismissed ?? [],
    autoArchivedIds: Object.keys(taskState.autoArchived ?? {}),
    autoOverrideIds: Object.keys(taskState.autoOverrides ?? {}),
    recurrenceIds: Object.keys(recurrence),
    prunedTaskStateIds: pruneResult?.removedIds ?? [],
    prunedRecurrenceIds: recurrenceResult?.removedIds ?? [],
    preservedGardenLevelIds: [
      ...(pruneResult?.preservedGardenLevelIds ?? []),
      ...(recurrenceResult?.preservedGardenLevelIds ?? []),
    ],
    preservedNonAutoIds: [
      ...(pruneResult?.preservedNonAutoIds ?? []),
      ...(recurrenceResult?.preservedNonAutoIds ?? []),
    ],
    preservedUnknownAutoIds: [
      ...(pruneResult?.preservedUnknownAutoIds ?? []),
      ...(recurrenceResult?.preservedUnknownAutoIds ?? []),
    ],
  });
}
